import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react'

import { controlClass } from '@/components/control-class'
import { useControl } from '@/components/field-control'
import { cx } from '@/lib/cx'
import { useT } from '@/lib/i18n/translate'
import { matchesQuery } from '@/lib/text-match'

export interface ComboboxOption {
  value: string
  label: string
}

/**
 * A select with type-to-search for long lists (zones, currencies): a text
 * field that filters a listbox under it. Closed, it shows the chosen label.
 * Works in a `Field` like `Select` does.
 */
export function Combobox({
  value,
  onChange,
  options,
  className,
}: {
  value: string
  onChange: (value: string) => void
  options: readonly ComboboxOption[]
  className?: string
}) {
  const t = useT()
  const control = useControl()
  const listId = useId()
  const input = useRef<HTMLInputElement>(null)
  // `null` = closed, showing the chosen label; a string = the query being typed.
  const [query, setQuery] = useState<string | null>(null)
  const [active, setActive] = useState(0)

  const selected = options.find((option) => option.value === value)
  const open = query !== null
  const matches = useMemo(() => (open ? options.filter((option) => matchesQuery(option.label, query)) : []), [open, options, query])

  function pick(option: ComboboxOption) {
    onChange(option.value)
    setQuery(null)
    input.current?.blur()
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        event.preventDefault()
        setQuery('')
      }
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((i) => Math.min(i + 1, matches.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const option = matches[active]
      if (option) pick(option)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setQuery(null)
    }
  }

  const optionId = (i: number) => `${listId}-${i}`

  // Focus highlights the current value, which may sit far down a long list.
  useEffect(() => {
    if (open) document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: 'nearest' })
  }, [open, active, listId])

  return (
    <div className={cx('relative', className)}>
      <input
        {...control}
        ref={input}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && matches[active] ? optionId(active) : undefined}
        placeholder={t('common.typeToSearch')}
        value={open ? query : (selected?.label ?? value)}
        onFocus={() => {
          setQuery('')
          setActive(Math.max(0, options.findIndex((option) => option.value === value)))
        }}
        onBlur={() => setQuery(null)}
        onChange={(event) => {
          setQuery(event.target.value)
          setActive(0)
        }}
        onKeyDown={onKeyDown}
        className={cx(controlClass, 'h-tap w-full')}
      />
      {open && (
        <ul
          id={listId}
          role="listbox"
          className="bg-surface border-border absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border py-1 shadow-lg"
        >
          {matches.map((option, i) => (
            <li
              key={option.value}
              id={optionId(i)}
              role="option"
              aria-selected={option.value === value}
              // Mouse down would blur the input and close the list before the click lands.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(option)}
              onMouseMove={() => setActive(i)}
              className={cx(
                'min-h-tap flex cursor-pointer items-center px-3 text-base',
                i === active && 'bg-accent-soft',
                option.value === value && 'font-medium',
              )}
            >
              {option.label}
            </li>
          ))}
          {matches.length === 0 && <li className="text-muted min-h-tap flex items-center px-3 text-sm">{t('common.noMatch')}</li>}
        </ul>
      )}
    </div>
  )
}
